// Whether the Notes Hub still owes this member the one-line "now end-to-end
// encrypted" banner (spec section 6.7, "Enrolment is silent").
//
// It is a one-shot per member, so it needs somewhere that survives the Hub's
// page cache and a reload but is not shared between the members of a shared
// phone: `localStorage`, keyed by member id, exactly as the platform copy is.
// Nothing here is a secret — the value is "pending" or "seen" — so this is the
// one piece of the encryption feature that is allowed to live in
// `localStorage` at all.
//
// Three states, spelled out because "absent" carries meaning:
//
//   absent    this member has never enrolled on this device: no banner.
//   pending   enrolment just succeeded: show it on the next Hub render.
//   seen      the member read it (or dismissed it): never show it again, and a
//             later `markBannerPending` must NOT bring it back. Enrolment
//             happens once per member, but a re-enrolment after "Start over"
//             would otherwise re-announce a promise the member already read.
//
// `seen` is written on the banner's FIRST RENDER as well as on dismissal
// (#1587): the line is one sentence of news, and a member who reads it without
// reaching for the close control has still read it. Writing it only on dismiss
// meant the banner came back on every Hub open for ever, which is not what
// spec section 6.7 promised.
//
// It is therefore a tombstone rather than a cache, and it deliberately survives
// sign-out — see the note in utils/clear-next-steps-caches.js.
//
// Every access is wrapped: `localStorage` throws outright in a locked-down
// WebView and in some private-browsing modes, and a banner is never a reason
// for the Hub to fail to render.

/** One entry per member id, so two members on one phone each get their own. */
export const BANNER_STORAGE_PREFIX = 'courses-e2ee-banner:';
export const BANNER_PENDING = 'pending';
export const BANNER_SEEN = 'seen';

/**
 * @param {unknown} memberId
 * @returns {string | null} null when the member is not known yet
 */
export function bannerStorageKey(memberId) {
  if (!Number.isInteger(memberId) || memberId <= 0) return null;
  return `${BANNER_STORAGE_PREFIX}${memberId}`;
}

/**
 * @param {string} key
 * @returns {string | null}
 */
function read(key) {
  try {
    return window.localStorage.getItem(key);
  } catch (error) {
    return null;
  }
}

/**
 * @param {string} key
 * @param {string} value
 */
function write(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch (error) {
    // A banner is not worth a thrown storage error; the member simply never
    // sees it on a device that cannot remember anything.
  }
}

/**
 * Enrolment succeeded: the Hub owes this member the banner once.
 *
 * @param {number} memberId
 */
export function markBannerPending(memberId) {
  const key = bannerStorageKey(memberId);
  if (!key) return;
  if (read(key) === BANNER_SEEN) return;
  write(key, BANNER_PENDING);
}

/**
 * @param {number} memberId
 * @returns {boolean}
 */
export function isBannerPending(memberId) {
  const key = bannerStorageKey(memberId);
  if (!key) return false;
  return read(key) === BANNER_PENDING;
}

/**
 * The banner reached the member: it rendered, or they tapped the close control.
 * Either way it is finished, and `markBannerPending` will not bring it back.
 *
 * @param {number} memberId
 */
export function markBannerSeen(memberId) {
  const key = bannerStorageKey(memberId);
  if (!key) return;
  write(key, BANNER_SEEN);
}

/**
 * The member tapped the close control. The same end state as reading it, kept
 * as its own name because that is what the call site means.
 *
 * @param {number} memberId
 */
export function dismissBanner(memberId) {
  markBannerSeen(memberId);
}
